import {
  AUTH_JWT_TOKEN,
  AUTH_JWT_TOKEN_EXPIRATION,
  AUTH_REFRESH_TOKEN,
  AUTH_REFRESH_TOKEN_EXPIRATION,
} from "@compass/core";
import axios, { AxiosInstance } from "axios";
import { store } from "redux/store";
import { AuthResult } from "generated/openApi";
import { setAppAuthStatus } from "redux/reducers/appSlice";
import { setAppLoading } from "redux/reducers/appSlice";
import { isKpiDataCreationRequest, notifyKpiDataChanged } from "services/kpiDataEvents";

export class InterceptorCoordinator {
  static baseUrl = process.env.REACT_APP_API_ENDPOINT;
  static requestsToRefresh: any[] = [];
  static isRefreshingToken: boolean = false;

  static initialize(instance: AxiosInstance) {
    this.addRequestInterceptor(instance);
    this.addResponseInterceptor(instance);
  }

  static addRequestInterceptor(instance: AxiosInstance) {
    instance.interceptors.request.use(
      (request) => {
        //console.log(request);

        // headers
        if (!request.headers) request.headers = {};
        if (!request.headers["Content-Type"]) request.headers["Content-Type"] = "application/json";
        if (!request.headers.Accept) request.headers.Accept = "*/*";
        if (!request.headers["X-Requested-With"]) request.headers["X-Requested-With"] = "XMLHttpRequest";

        // method
        if (!request.method) request.method = "GET";

        if (!request.headers.Authorization) {
          const jwtToken: string | null = localStorage.getItem(AUTH_JWT_TOKEN);
          if (jwtToken) {
            request.headers.Authorization = `BEARER ${jwtToken}`;
          }
        }

        if (!request.noLoading) {
          const appLoading: number = store.getState().app.loading;
          store.dispatch(setAppLoading(appLoading + 1));
        }

        return request;
      },
      (error) => {
        if (!error.requestConfig.noLoading) {
          const appLoading: number = store.getState().app.loading;
          store.dispatch(setAppLoading(appLoading - 1));
        }

        return Promise.reject(error);
      }
    );
  }

  static addResponseInterceptor(instance: AxiosInstance) {
    instance.interceptors.response.use(
      (response) => {
        if (!response.config.noLoading) {
          const appLoading: number = store.getState().app.loading;
          store.dispatch(setAppLoading(appLoading - 1));
        }

        if (isKpiDataCreationRequest(response.config.method, response.config.url)) {
          const { tenantName, userId } = store.getState().app;
          notifyKpiDataChanged(tenantName, userId);
        }

        return response;
      },
      (error) => {
        if (!error.config.noLoading) {
          const appLoading: number = store.getState().app.loading;
          store.dispatch(setAppLoading(appLoading - 1));
        }

        // 400: bad request, return the error response
        if (error.response && error.response.status === 400) {
          // console.log("here", error.response);
          // error.response.data = JSON.parse(error.response.data);
          return error.response;
        }

        // 401: unauthorized: use refreshToken to get a new jwtToken and try to authorize again
        if (error.response && error.response.status === 401) {
          // this call should set the JwtToken in the cookie if successful
          const refreshToken: string | null = localStorage.getItem(AUTH_REFRESH_TOKEN);

          // If we have not logged in before, it makes no sense to try to get a new token
          if (!refreshToken) {
            store.dispatch(setAppAuthStatus("NOT_AUTHENTICATED")); // set isAuthenticate to false
            return Promise.reject(error);
          }

          // isRefreshingToken prevents multiple concurrent requests from trying to refresh the tokens (and get back different tokens)
          if (!InterceptorCoordinator.isRefreshingToken) {
            InterceptorCoordinator.isRefreshingToken = true;

            axios
              .get(`${InterceptorCoordinator.baseUrl}/auth/refreshToken?refreshTokenString=${refreshToken}`)
              .then(({ data }) => {
                const authResult: AuthResult = data;
                localStorage.setItem(AUTH_JWT_TOKEN, authResult.jwtToken!);
                localStorage.setItem(AUTH_JWT_TOKEN_EXPIRATION, authResult.jwtTokenExpiration?.toString()!);
                localStorage.setItem(AUTH_REFRESH_TOKEN, authResult.refreshToken!);
                localStorage.setItem(AUTH_REFRESH_TOKEN_EXPIRATION, authResult.refreshTokenExpiration?.toString()!);

                // execute original requests to be called later once token is fetched
                InterceptorCoordinator.requestsToRefresh.forEach((cb) => cb(authResult.jwtToken!));
              })
              .catch(() => {
                store.dispatch(setAppAuthStatus("NOT_AUTHENTICATED")); // set isAuthenticate to false
              })
              .finally(() => {
                InterceptorCoordinator.requestsToRefresh = [];
                InterceptorCoordinator.isRefreshingToken = false;
              });
          }

          return new Promise((resolve, reject) => {
            // requestsToRefresh (requests that expect a new token from the first request), we add a callback which the first request to execute
            // these functions won't be executed until the first request finishes refreshing token
            InterceptorCoordinator.requestsToRefresh.push((jwtToken: string) => {
              if (jwtToken) {
                error.config.headers.Authorization = `BEARER ${jwtToken}`;
                resolve(instance(error.config));
              }

              // If the first request could not refresh the token, we must return the basic request processing logic
              reject(error);
            });
          });
        }

        if (error.response && error.response.data) {
          alert(error.response.data.message);
        } else {
          alert("An unexpected error occured.");
        }

        return Promise.reject(error.response || error.message);
      }
    );
  }
}
